/* eslint-disable @typescript-eslint/no-explicit-any, react-hooks/rules-of-hooks, react-hooks/exhaustive-deps, eqeqeq */

import { ConnectForm, ControlledComponentProps, Label } from "@/components/Controlled"
import { ErrorMessages } from "@/components/Controlled/ErrorMessages"
import { isItEmpty } from "@/helpers/genericHelpers"
import type { TextareaProps } from "@mantine/core"
import { Textarea as MantineTextarea } from "@mantine/core"
import React from "react"
import { useController } from "react-hook-form"

export const TextareaInput = ({
    label,
    name,
    defaultValue,
    disabled,
    readOnly = false,
    rules,
    shouldUnregister,
    tooltip,
    triggerOnChange = true,
    showError = true,
    withAsterisk,
    ...props
}: ControlledComponentProps<TextareaProps & { triggerOnChange?: boolean; showError?: boolean }>) => (
    <ConnectForm>
        {({ control, trigger }) => {
            const { field, fieldState } = useController({ name, control, defaultValue, rules, shouldUnregister })
            const className = readOnly ? `${props.className} read-only` : props.className
            const required = !readOnly && (props.required || Boolean(rules?.required))
            const onChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
                field.onChange(event)
                const isEmpty = field.value === "" // This removes required error state on the first typed letter after the error for onBlur validations
                if (triggerOnChange || isEmpty) {
                    trigger(name)
                }
            }

            /* This workaround is necessary because the onFocus on controlled field is overriding the onBlur from props.
            It is due to the validation is tied to controlled field events */
            const onBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
                field.onBlur()
                if (props.onBlur) props.onBlur(event)
            }

            return (
                <div>
                    <MantineTextarea
                        {...props}
                        {...field}
                        className={className}
                        // adding disabled here, field is overriding props with undefined
                        disabled={disabled}
                        onBlur={onBlur}
                        label={label && <Label label={label} tooltip={tooltip} />}
                        onChange={onChange}
                        error={fieldState.error && showError && <ErrorMessages fieldError={fieldState.error} />}
                        aria-invalid={Boolean(fieldState.error)}
                        id={name}
                        required={required}
                        value={isItEmpty(field.value) ? defaultValue : field.value}
                    />
                </div>
            )
        }}
    </ConnectForm>
)
